import React, { useRef } from 'react';
import { Image, ChevronRight } from 'lucide-react';

interface ImageUploadPanelProps {
  onUpload: (file: File) => void;
  onSelectFromLibrary: () => void;
  onSelectEmoji: () => void;
  onSelectRecentImage?: (imageUrl: string) => void;
  onClose: () => void;
  recentImages?: string[];
}

export const ImageUploadPanel: React.FC<ImageUploadPanelProps> = ({
  onUpload,
  onSelectFromLibrary,
  onSelectEmoji,
  onSelectRecentImage,
  recentImages = [],
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleVideoUploadClick = () => {
    videoInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div className="flex flex-row items-stretch">
      {/* Left Panel - Options */}
      <div className="flex flex-col items-start p-6 gap-6 w-[238px] bg-[rgba(32,40,53,0.9)] border-2 border-[#DFE4EA] backdrop-blur-[12.5px] rounded-l-lg">
        {/* Header */}
        <div className="flex flex-row items-center gap-[6px]">
          <Image size={24} className="text-[#DFE4EA]" />
          <h3 className="font-medium text-[20px] leading-[22px] text-[#DFE4EA]">
            Add Image
          </h3>
        </div>

        {/* Options */}
        <div className="flex flex-col gap-10 w-full">
          {/* Upload Image */}
          <button
            onClick={handleUploadClick}
            className="flex flex-row items-center justify-between w-full group"
          >
            <span className="font-medium text-[16px] leading-[22px] text-[#DFE4EA] group-hover:text-white transition-colors">
              Upload Image
            </span>
            <ChevronRight size={12} className="text-[#DFE4EA] rotate-90" />
          </button>

          {/* Upload Video */}
          <button
            onClick={handleVideoUploadClick}
            className="flex flex-row items-center justify-between w-full group"
          >
            <span className="font-medium text-[16px] leading-[22px] text-[#DFE4EA] group-hover:text-white transition-colors">
              Upload a Video
            </span>
            <ChevronRight size={12} className="text-[#DFE4EA] rotate-90" />
          </button>

          {/* Select from Library */}
          <button
            onClick={onSelectFromLibrary}
            className="flex flex-row items-center justify-between w-full group"
          >
            <span className="font-medium text-[16px] leading-[22px] text-[#DFE4EA] group-hover:text-white transition-colors">
              Select from Library
            </span>
            <ChevronRight size={12} className="text-[#DFE4EA] rotate-90" />
          </button>

          {/* Emoji */}
          <button
            onClick={onSelectEmoji}
            className="flex flex-row items-center justify-between w-full group"
          >
            <span className="font-medium text-[16px] leading-[22px] text-[#DFE4EA] group-hover:text-white transition-colors">
              Emoji
            </span>
            <ChevronRight size={12} className="text-[#DFE4EA] rotate-90" />
          </button>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Right Panel - Recent */}
      <div className="flex flex-col items-center p-6 gap-6 w-[402px] bg-[rgba(32,40,53,0.91)] border-2 border-l-0 border-[#DFE4EA] backdrop-blur-[12.5px] rounded-r-lg">
        <h4 className="font-medium text-[16px] leading-[22px] text-[#DFE4EA] w-full text-left">
          Recent
        </h4>

        {/* Recent Images Grid */}
        <div className="flex flex-row items-center gap-[23px]">
          {recentImages.length > 0 ? (
            recentImages.slice(0, 4).map((imageUrl, index) => (
              <button
                key={index}
                onClick={() => onSelectRecentImage?.(imageUrl)}
                className="box-border bg-[rgba(223,228,234,0.2)] border-2 border-[#DFE4EA] rounded-[2px] hover:bg-[rgba(223,228,234,0.3)] transition-colors overflow-hidden"
                style={{
                  width: index === 1 ? '58px' : '59px',
                  height: index === 1 ? '86px' : index === 2 ? '32px' : '59px',
                }}
              >
                <img
                  src={imageUrl}
                  alt={`Recent ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))
          ) : (
            <>
              <div className="box-border w-[59px] h-[59px] bg-[rgba(223,228,234,0.2)] border-2 border-[#DFE4EA] rounded-[2px]" />
              <div className="box-border w-[58px] h-[86px] bg-[rgba(223,228,234,0.2)] border-2 border-[#DFE4EA] rounded-[2px]" />
              <div className="box-border w-[58px] h-[32px] bg-[rgba(223,228,234,0.2)] border-2 border-[#DFE4EA] rounded-[2px]" />
              <div className="box-border w-[59px] h-[59px] bg-[rgba(223,228,234,0.2)] border-2 border-[#DFE4EA] rounded-[2px]" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
